import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from 'firebase/firestore'
import {
  RepairTruck,
  RepairTruckInspectionChecklist,
  RepairTruckChecklistAnswer,
  RepairTruckChecklistResponse,
  RepairTruckChecklistWalkaroundVideo,
  RepairTruckWorkNote,
  RepairTruckWorkStatus,
  RepairTruckWorkStatusHistoryEntry,
  RepairWorkType,
} from '../logistics/logistics'

const VALID_WORK_STATUSES: RepairTruckWorkStatus[] = [
  'Awaiting estimate',
  'in progress',
  'complete',
]

const VALID_WORK_TYPES: RepairWorkType[] = ['body', 'mechanical']

const VALID_CHECKLIST_ANSWERS: RepairTruckChecklistAnswer[] = ['yes', 'no', 'na']

function parseWorkType(raw: unknown): RepairWorkType | null {
  return VALID_WORK_TYPES.includes(raw as RepairWorkType) ? (raw as RepairWorkType) : null
}

function parseWorkNotes(raw: unknown): RepairTruckWorkNote[] {
  if (!Array.isArray(raw)) return []
  const result: RepairTruckWorkNote[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const obj = entry as Record<string, unknown>
    const note = typeof obj.note === 'string' ? obj.note : ''
    const timestamp = obj.timestamp
    const user_id = typeof obj.user_id === 'string' ? obj.user_id : ''
    const user_name = typeof obj.user_name === 'string' ? obj.user_name : undefined
    const visible_to_repair_users = obj.visible_to_repair_users === true ? true : undefined
    const rawMedia = obj.media
    let media: RepairTruckWorkNote['media']
    if (rawMedia && typeof rawMedia === 'object') {
      const m = rawMedia as Record<string, unknown>
      const storage_path = typeof m.storage_path === 'string' ? m.storage_path : ''
      const resolvedType =
        m.type === 'video'
          ? 'video'
          : m.type === 'photo'
            ? 'photo'
            : m.type === 'invoice'
              ? 'invoice'
              : m.type === 'estimate'
                ? 'estimate'
                : m.type === 'damage_photo'
                  ? 'damage_photo'
                  : undefined
      if (storage_path && resolvedType) {
        media = { type: resolvedType, storage_path }
      }
    }
    if (timestamp != null && user_id) {
      const rawReview = obj.estimate_review
      let estimate_review: RepairTruckWorkNote['estimate_review']
      if (rawReview && typeof rawReview === 'object') {
        const r = rawReview as Record<string, unknown>
        const status =
          r.status === 'approved' || r.status === 'rejected' ? r.status : undefined
        const decided_by = typeof r.decided_by === 'string' ? r.decided_by : ''
        if (status && decided_by && r.decided_at != null) {
          estimate_review = {
            status,
            decided_at: r.decided_at as Timestamp,
            decided_by,
            ...(typeof r.note === 'string' && r.note.trim() ? { note: r.note.trim() } : {}),
          }
        }
      }
      result.push({
        note,
        timestamp: timestamp as Timestamp,
        user_id,
        ...(user_name ? { user_name } : {}),
        ...(visible_to_repair_users ? { visible_to_repair_users: true } : {}),
        ...(media ? { media } : {}),
        ...(estimate_review ? { estimate_review } : {}),
      })
    }
  }
  return result
}

function parseWorkStatus(raw: unknown): RepairTruckWorkStatus {
  if (VALID_WORK_STATUSES.includes(raw as RepairTruckWorkStatus)) {
    return raw as RepairTruckWorkStatus
  }
  return 'Awaiting estimate'
}

function parseWorkStatusHistory(raw: unknown): RepairTruckWorkStatusHistoryEntry[] {
  if (!Array.isArray(raw)) return []
  const result: RepairTruckWorkStatusHistoryEntry[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const obj = entry as Record<string, unknown>
    const status = parseWorkStatus(obj.status)
    const timestamp = obj.timestamp
    const user_id = typeof obj.user_id === 'string' ? obj.user_id : ''
    if (timestamp != null && user_id) {
      result.push({
        status,
        timestamp: timestamp as Timestamp,
        user_id,
      })
    }
  }
  return result
}

function parseChecklistPhotos(raw: unknown): RepairTruckChecklistResponse['photos'] {
  if (!Array.isArray(raw)) return undefined
  const photos: NonNullable<RepairTruckChecklistResponse['photos']> = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const obj = entry as Record<string, unknown>
    const storage_path = typeof obj.storage_path === 'string' ? obj.storage_path.trim() : ''
    if (!storage_path) continue
    const media_type =
      obj.media_type === 'video' || obj.media_type === 'photo'
        ? obj.media_type
        : undefined
    const content_type =
      typeof obj.content_type === 'string' && obj.content_type.trim()
        ? obj.content_type.trim()
        : undefined
    photos.push({
      storage_path,
      uploaded_at: obj.uploaded_at as Timestamp | undefined,
      uploaded_by: typeof obj.uploaded_by === 'string' ? obj.uploaded_by : undefined,
      ...(media_type ? { media_type } : {}),
      ...(content_type ? { content_type } : {}),
    })
  }
  return photos.length > 0 ? photos : undefined
}

function parseChecklistResponse(raw: unknown): RepairTruckChecklistResponse | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const answer = VALID_CHECKLIST_ANSWERS.includes(obj.answer as RepairTruckChecklistAnswer)
    ? (obj.answer as RepairTruckChecklistAnswer)
    : undefined
  const notes = typeof obj.notes === 'string' ? obj.notes : undefined
  const answered_at = obj.answered_at as Timestamp | undefined
  const answered_by = typeof obj.answered_by === 'string' ? obj.answered_by : undefined
  const photos = parseChecklistPhotos(obj.photos)
  return {
    answer,
    notes,
    answered_at,
    answered_by,
    ...(photos ? { photos } : {}),
  }
}

function parseWalkaroundVideo(raw: unknown): RepairTruckChecklistWalkaroundVideo | null | undefined {
  if (raw === null) return null
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const storage_path = typeof obj.storage_path === 'string' ? obj.storage_path.trim() : ''
  if (!storage_path) return undefined
  const content_type =
    typeof obj.content_type === 'string' && obj.content_type.trim()
      ? obj.content_type.trim()
      : undefined
  return {
    storage_path,
    uploaded_at: obj.uploaded_at as Timestamp | undefined,
    uploaded_by: typeof obj.uploaded_by === 'string' ? obj.uploaded_by : undefined,
    ...(content_type ? { content_type } : {}),
  }
}

function parseInspectionChecklist(raw: unknown): RepairTruckInspectionChecklist | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const template_id = typeof obj.template_id === 'string' ? obj.template_id : 'default'
  const template_updated_at = (obj.template_updated_at as Timestamp | null | undefined) ?? null
  const completed_at = (obj.completed_at as Timestamp | null | undefined) ?? null
  const walkaround_video = parseWalkaroundVideo(obj.walkaround_video)
  const responsesRaw = obj.responses
  const responses: Record<string, RepairTruckChecklistResponse> = {}

  if (responsesRaw && typeof responsesRaw === 'object' && !Array.isArray(responsesRaw)) {
    Object.entries(responsesRaw as Record<string, unknown>).forEach(([questionId, value]) => {
      const parsed = parseChecklistResponse(value)
      if (parsed) {
        responses[questionId] = parsed
      }
    })
  }

  return {
    template_id,
    template_updated_at,
    responses,
    ...(walkaround_video !== undefined ? { walkaround_video } : {}),
    completed_at,
  }
}

export const repairTruckConverter: FirestoreDataConverter<RepairTruck> = {
  toFirestore(repair: WithFieldValue<RepairTruck>) {
    const serializeChecklist = (
      checklist: RepairTruckInspectionChecklist | undefined
    ): Record<string, unknown> | undefined => {
      if (!checklist) return undefined
      const responsesPayload: Record<string, Record<string, unknown>> = {}
      Object.entries(checklist.responses ?? {}).forEach(([questionId, response]) => {
        responsesPayload[questionId] = {
          ...(response.answer ? { answer: response.answer } : {}),
          ...(typeof response.notes === 'string' ? { notes: response.notes } : {}),
          ...(response.answered_at ? { answered_at: response.answered_at } : {}),
          ...(response.answered_by ? { answered_by: response.answered_by } : {}),
          ...(Array.isArray(response.photos) && response.photos.length > 0
            ? {
                photos: response.photos
                  .filter((photo) => Boolean(photo?.storage_path))
                  .map((photo) => ({
                    storage_path: photo.storage_path,
                    ...(photo.uploaded_at ? { uploaded_at: photo.uploaded_at } : {}),
                    ...(photo.uploaded_by ? { uploaded_by: photo.uploaded_by } : {}),
                    ...(photo.media_type ? { media_type: photo.media_type } : {}),
                    ...(photo.content_type ? { content_type: photo.content_type } : {}),
                  })),
              }
            : {}),
        }
      })
      return {
        template_id: checklist.template_id ?? 'default',
        template_updated_at: checklist.template_updated_at ?? null,
        completed_at: checklist.completed_at ?? null,
        responses: responsesPayload,
        ...(checklist.walkaround_video?.storage_path
          ? {
              walkaround_video: {
                storage_path: checklist.walkaround_video.storage_path,
                ...(checklist.walkaround_video.uploaded_at
                  ? { uploaded_at: checklist.walkaround_video.uploaded_at }
                  : {}),
                ...(checklist.walkaround_video.uploaded_by
                  ? { uploaded_by: checklist.walkaround_video.uploaded_by }
                  : {}),
                ...(checklist.walkaround_video.content_type
                  ? { content_type: checklist.walkaround_video.content_type }
                  : {}),
              },
            }
          : checklist.walkaround_video === null
            ? { walkaround_video: null }
            : {}),
      }
    }

    const payload: Record<string, unknown> = {
      transport_truck_id: repair.transport_truck_id ?? '',
      repair_shop_location_id: repair.repair_shop_location_id ?? '',
      priority_rank:
        typeof repair.priority_rank === 'number' && Number.isFinite(repair.priority_rank)
          ? repair.priority_rank
          : null,
      work_type: parseWorkType(repair.work_type),
      work_notes: Array.isArray(repair.work_notes)
        ? (repair.work_notes as RepairTruckWorkNote[]).map((e) => ({
            note: e.note ?? '',
            timestamp: e.timestamp ?? Timestamp.now(),
            user_id: e.user_id ?? '',
            ...(e.user_name ? { user_name: e.user_name } : {}),
            ...(e.visible_to_repair_users ? { visible_to_repair_users: true } : {}),
            ...(e.media?.storage_path &&
            (e.media.type === 'photo' ||
              e.media.type === 'video' ||
              e.media.type === 'invoice' ||
              e.media.type === 'estimate' ||
              e.media.type === 'damage_photo')
              ? {
                  media: {
                    type: e.media.type,
                    storage_path: e.media.storage_path,
                  },
                }
              : {}),
            ...(e.estimate_review?.status &&
            e.estimate_review.decided_by &&
            e.estimate_review.decided_at
              ? {
                  estimate_review: {
                    status: e.estimate_review.status,
                    decided_at: e.estimate_review.decided_at,
                    decided_by: e.estimate_review.decided_by,
                    ...(e.estimate_review.note ? { note: e.estimate_review.note } : {}),
                  },
                }
              : {}),
          }))
        : [],
      created_at: repair.created_at ?? Timestamp.now(),
      work_status_history: Array.isArray(repair.work_status_history)
        ? (repair.work_status_history as RepairTruckWorkStatusHistoryEntry[]).map((e) => ({
            status: parseWorkStatus(e.status),
            timestamp: e.timestamp ?? Timestamp.now(),
            user_id: e.user_id ?? '',
          }))
        : [],
      flagged: Boolean(repair.flagged),
      paid: repair.paid ?? false,
      paid_date: repair.paid_date ?? null,
      being_painted: repair.being_painted ?? false,
      being_painted_date: repair.being_painted_date ?? null,
      ...(repair.requires_intake === true ? { requires_intake: true } : {}),
    }

    const completeChecklist = serializeChecklist(
      repair.inspection_checklist as unknown as RepairTruckInspectionChecklist | undefined
    )
    if (completeChecklist) {
      payload.inspection_checklist = completeChecklist
    }

    const intakeChecklist = serializeChecklist(
      repair.intake_checklist as unknown as RepairTruckInspectionChecklist | undefined
    )
    if (intakeChecklist) {
      payload.intake_checklist = intakeChecklist
    }

    return payload
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): RepairTruck {
    const data = snapshot.data(options)
    return {
      id: snapshot.id,
      transport_truck_id: data.transport_truck_id ?? '',
      repair_shop_location_id: data.repair_shop_location_id ?? '',
      priority_rank:
        typeof data.priority_rank === 'number' && Number.isFinite(data.priority_rank)
          ? data.priority_rank
          : null,
      work_type: parseWorkType(data.work_type),
      work_notes: parseWorkNotes(data.work_notes),
      created_at: data.created_at ?? Timestamp.now(),
      work_status_history: parseWorkStatusHistory(data.work_status_history),
      flagged: Boolean(data.flagged),
      paid: Boolean(data.paid),
      paid_date: data.paid_date ?? null,
      being_painted: Boolean(data.being_painted),
      being_painted_date: data.being_painted_date ?? null,
      requires_intake: data.requires_intake === true,
      intake_checklist: parseInspectionChecklist(data.intake_checklist),
      inspection_checklist: parseInspectionChecklist(data.inspection_checklist),
    }
  },
}
